import { AI_BASE_URL } from "../config";

export interface Language {
  code: string;
  name: string;
  flag_url: string;
  is_chat_default?: boolean;
}

export interface UrlSuggestionsResponse {
  url: string;
  questions: string[];
}

export interface PageGreetingResponse {
  file_id?: string;
  video_file_id?: string;
  content?: string;
}

export interface ChatSessionMessage {
  custom_id: string;
  content: string;
  chat_session_id: string;
  output_language: string;
  id: string;
  user_id: string;
  context: string;
  is_human: boolean;
  audio_file_id?: string;
  video_file_id?: string;
  created_at: string;
}

export interface ChatSessionMessagesResponse {
  items: ChatSessionMessage[];
  total: number;
  page: number;
  size: number;
  pages: number;
}

/**
 * Fetches available languages for a user
 * @param userId - The user ID
 * @returns Promise with array of languages, or empty array on error
 */
export async function getLanguages(
  userId: string
): Promise<Language[]> {
  try {
    const res = await fetch(
      `${AI_BASE_URL}/languages?user_id=${encodeURIComponent(userId)}`
    );
    if (!res.ok) return [];
    
    const data = (await res.json()) as Language[];
    return data || [];
  } catch {
    return [];
  }
}

/**
 * Fetches page greeting text/content for a user
 * @param userId - The user ID
 * @returns Promise with page greeting data, or null on error
 */
export async function getPageGreeting(
  userId: string
): Promise<PageGreetingResponse | null> {
  try {
    const url = `${AI_BASE_URL}/users/public/page_greeting?user_id=${encodeURIComponent(
      userId
    )}`;
    const res = await fetch(url, { headers: { accept: "application/json" } });
    if (!res.ok) return null;
    
    const data = (await res.json()) as PageGreetingResponse;
    return data;
  } catch {
    return null;
  }
}

/**
 * Fetches suggested questions for a given URL.
 * The API scrapes the URL and generates relevant questions using GPT.
 * Results are cached to avoid re-scraping on every request.
 * 
 * @param url - The URL to get suggestions for
 * @returns Promise resolving to the suggestions response, or null if the request fails
 */
export async function getUrlSuggestions(
  url: string
): Promise<UrlSuggestionsResponse | null> {
  try {
    const response = await fetch(`${AI_BASE_URL}/url-suggestions`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
      },
      body: JSON.stringify({ url }),
    });

    if (!response.ok) {
      return null;
    }

    const data = await response.json();
    
    // Validate response structure
    if (data && Array.isArray(data.questions) && data.questions.length > 0) {
      return data as UrlSuggestionsResponse;
    }

    return null;
  } catch (error) {
    console.warn("Failed to fetch URL suggestions:", error);
    return null;
  }
}

/**
 * Fetches chat session messages for a guest user
 * @param userDeviceId - The device ID of the user
 * @param aiUserId - The AI user/AI rep ID
 * @param page - Page number (default: 1)
 * @param size - Page size (default: 50, max: 100)
 * @param markRead - Whether to mark messages as read (default: false)
 * @returns Promise with chat session messages response, or null on error
 */
export async function getChatSessionMessages(
  userDeviceId: string,
  aiUserId: string,
  page: number = 1,
  size: number = 50,
  markRead: boolean = false
): Promise<ChatSessionMessagesResponse | null> {
  try {
    const params = new URLSearchParams({
      user_device_id: userDeviceId,
      ai_user_id: aiUserId,
      page: page.toString(),
      size: Math.min(size, 100).toString(),
      mark_read: markRead.toString(),
    });

    const response = await fetch(`${AI_BASE_URL}/chat_sessions/messages/guest?${params.toString()}`, {
      method: "GET",
      headers: {
        "Accept": "application/json",
      },
    });

    if (!response.ok) {
      return null;
    }

    const data = await response.json() as ChatSessionMessagesResponse;
    return data;
  } catch (error) {
    console.warn("Failed to fetch chat session messages:", error);
    return null;
  }
}

